import React, { useState } from 'react'
import {
  Flex,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Select,
  Typography,
} from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { filterOption } from 'src/pages/finSystem/debt/utils'
import { useOptionsWallet } from 'src/pages/finSystem/debt/hook'
import { getExchangeRateFx } from 'src/pages/finSystem/debt/model'
import { useUnit } from 'effector-react'
import { DebtPayload } from 'src/pages/finSystem/debt/types'
import {
  CommentField,
  DateTimeRow,
} from '../../wallets/atoms/transactionFields'
import dayjs from 'dayjs'

type Props = {
  form: FormInstance
  initialValues?: Partial<DebtPayload>
  walletDisabled?: boolean
}

export const DebtForm = ({ form, initialValues, walletDisabled }: Props) => {
  const [symbolRate, setSymbolRate] = useState<null | {
    rate: string
    symbol: string
  }>(null)

  const { Item } = Form
  const { Text } = Typography

  const exchangeLoading = useUnit(getExchangeRateFx.pending)

  const optionsWallet = useOptionsWallet()

  const currentWalletId = Form.useWatch('wallet', form)

  const currentWallet = optionsWallet.find(
    (wallet: any) => wallet.value === currentWalletId,
  )
  const optionsCurrency =
    currentWallet?.localBalances?.map(({ currency }: { currency: string }) => ({
      value: currency,
      label: currency,
    })) ?? []

  const initialDateTime = initialValues?.dateTime
    ? dayjs(initialValues.dateTime)
    : undefined
  const formInitialValues = {
    date: initialDateTime ?? dayjs(),
    time: initialDateTime ?? dayjs(),
    ...initialValues,
  }

  const handleCurrencyChange = async (value: string) => {
    setSymbolRate(null)
    form.setFieldValue('exchangeRate', undefined)

    if (value.toUpperCase() === 'USD') {
      setSymbolRate({
        rate: '1',
        symbol: 'USD-USD',
      })
      form.setFieldValue('exchangeRate', 1)
      return
    }
    try {
      const { body } = await getExchangeRateFx({
        body: {
          baseCurrency: 'USD',
          quoteCurrency: value,
          amount: '1',
        },
      })
      if (body) {
        const { rate, symbol } = body
        setSymbolRate({ rate, symbol })
      }
    } catch (error) {}
  }
  return (
    <Form form={form} layout="vertical" initialValues={formInitialValues}>
      <Item
        label={
          <Text style={{ fontSize: 16 }} strong>
            Wallet
          </Text>
        }
        name="wallet"
        key="wallet"
        required={false}
        rules={[
          {
            required: true,
            message: 'Required field',
          },
        ]}
      >
        <Select
          placeholder="Wallet"
          options={optionsWallet}
          filterOption={filterOption}
          disabled={walletDisabled}
        />
      </Item>
      <Flex justify="space-between" gap={16}>
        <Item
          label={
            <Text style={{ fontSize: 16 }} strong>
              Debt Amount
            </Text>
          }
          name="debtAmount"
          key="debtAmount"
          required={false}
          style={{ width: '50%', minHeight: 85 }}
          tooltip="Positive amount: funds will be withdrawn from the wallet. Negative amount: funds will be edited to the wallet"
          rules={[
            {
              required: true,
              message: 'Required field',
            },
            {
              validator(_, value) {
                if (value === 0) {
                  return Promise.reject(new Error('The value cannot be 0'))
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          <InputNumber style={{ width: '100%' }} />
        </Item>
        <Item
          label={
            <Text style={{ fontSize: 16 }} strong>
              Recovered Amount
            </Text>
          }
          name="recoveredAmount"
          required={false}
          style={{ width: '50%', minHeight: 85 }}
          dependencies={['debtAmount']}
          rules={[
            {
              required: true,
              message: 'Required field',
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const debtAmount = getFieldValue('debtAmount')

                if (
                  debtAmount === undefined ||
                  debtAmount === null ||
                  value === undefined ||
                  value === null
                ) {
                  return Promise.resolve()
                }

                if (
                  (debtAmount > 0 && value < 0) ||
                  (debtAmount < 0 && value > 0)
                ) {
                  return Promise.reject(
                    new Error(
                      'Recovered Amount must have the same sign as Debt Amount',
                    ),
                  )
                }

                if (Math.abs(value) > Math.abs(debtAmount)) {
                  return Promise.reject(
                    new Error('Recovered Amount cannot exceed the Debt Amount'),
                  )
                }

                return Promise.resolve()
              },
            }),
          ]}
        >
          <InputNumber style={{ width: '100%' }} />
        </Item>
      </Flex>
      <Flex justify="space-between" gap={16}>
        <Item
          label={
            <Text style={{ fontSize: 16 }} strong>
              Currency
            </Text>
          }
          name="currency"
          key="currency"
          required={false}
          style={{ width: '50%' }}
          rules={[
            {
              required: true,
              message: 'Required field',
            },
          ]}
        >
          <Select
            placeholder="Currency"
            options={optionsCurrency}
            filterOption={filterOption}
            onChange={handleCurrencyChange}
          />
        </Item>
        <Item
          label={
            <Text style={{ fontSize: 16 }} strong>
              Exchange Rate{' '}
            </Text>
          }
          tooltip={
            <span>
              Leave empty field to use the exchange rate at creation time from
              BE, or enter a custom rate.
              <br /> Current exchange rate:
              <br />
              {exchangeLoading ? (
                <LoadingOutlined style={{ color: 'var(--color-primary)' }} />
              ) : (
                <Text style={{ fontSize: 16, color: 'var(--color-primary)' }}>
                  {symbolRate?.symbol.slice(0, 3)} -{' '}
                  {symbolRate?.symbol.slice(3, symbolRate?.symbol.length)}{' '}
                  {symbolRate?.rate}
                </Text>
              )}
            </span>
          }
          name="exchangeRate"
          key="exchangeRate"
          required={false}
          style={{ width: '50%' }}
        >
          <InputNumber style={{ width: '100%' }} />
        </Item>
      </Flex>
      <Flex justify="space-between" gap={16}>
        <Item
          label={
            <Text style={{ fontSize: 16 }} strong>
              User ID
            </Text>
          }
          name="userId"
          key="userId"
          required={false}
          style={{ width: '50%' }}
          tooltip="An empty field may indicate a manager error or a technical issue"
        >
          <Input placeholder="User ID" style={{ width: '100%' }} />
        </Item>
      </Flex>
      <DateTimeRow />
      <CommentField />
      <Item
        name="status"
        style={{ width: '50%' }}
        label={
          <Text style={{ fontSize: 16 }} strong>
            Status
          </Text>
        }
      >
        <Select
          options={[
            { label: 'Active', value: 'Active' },
            { label: 'Closed', value: 'Closed' },
          ]}
        />
      </Item>
    </Form>
  )
}
